/**
 * **ステータスの変化を疑似イベント `stat_change` にする**（Issue #584）。**`duelEditor/` の外**。
 *
 * core はステータスの変化を MSG に出さない。再現エンジン（`ocgcore/service/board_steps.py::_stat_step`）が処理の区切りごとに
 * モンスターゾーンを query し、`board_steps` のステップに `stats`（今の値）と `stat_changes`（場に居続けたまま値が変わった瞬間）を
 * 載せる。演出（`duelEditor/themes/<theme>/effects/stat_change.js`）はイベントにしか付かないので、ここで**イベントの列に 1 行差し込む**:
 *
 * - **どこに**: そのステップ（区切りの最後の MSG）以前の最後の core のイベント（`kind: 'event'`）の**直後**。
 *   区切りの最後が問い合わせ（prompt）のこともあり、そのまま付けると次の応答の時刻（答えた時刻）になるので、
 *   直前の core のイベントに付ける（旧演出プレビュー（Issue #639 で削除）の `effects/stat.js` の「キーの無いイベントなら直前のキーのあるイベントに付ける」と同じ）
 * - **キー**: 元のイベントと同じ応答（`op_seq`）で、`msg_offset` に `STAT_CHANGE_OFFSET`（100000）を足した値。
 *   キーは `{op_uuid}:{msg_offset}`（`ocgCore` の `eventKeys`）なので既存のキーとぶつからず、サーバーの検証
 *   （`ocg/fx_docs.py` の `_EVENT_KEY` = 数字 6 桁まで）も通る（1 つの応答の MSG が 10 万を超えることは無い）
 * - **index**: ステップの index + 0.5（盤面の時系列 `boardTimeline.js` は index でイベントの時刻を引くので、
 *   ステータスの値は**この行の時刻に**変わる。演出が `board: { at }` で数え終わるまで遅らせられる）
 * - **盤面のステップを分ける**: 変わったカードの `stats` だけを元のステップから抜き、index + 0.5 の新しいステップに移す
 *   （同じ区切りで場に出たカードの基準の値・ゾーンの変化は元の時刻のまま）
 * - **元々の値を補う**: サービスが `stats` に `base_atk` / `base_def` を載せていない（古いサービス）ときは `stat_changes` の `base` で補う
 *
 * payload（`data.cards`）: `[{ uid, code, loc: { controller, location: 4, sequence }, controller, from: { atk, def, level }, to: {…}, base: { atk, def } }]`
 * （同じ区切りで何体も変われば 1 行にまとめる）。**出す条件は絞らない**（原因を問わず、場に居続けたモンスターの値が変わったら出す）。
 */

export const STAT_CHANGE_TYPE = 'stat_change';
/** 疑似イベントの `msg_offset` に足す値（既存のキーとぶつからない・サーバーの検証の 6 桁に入る） */
export const STAT_CHANGE_OFFSET = 100000;

const LOCATION_MZONE = 4;
const num = (v) => (Number.isFinite(v) ? v : null);

/** ステップの `stat_changes` → 疑似イベントの `data.cards` */
function statCards(changes) {
  return changes.map((ch) => ({
    uid: ch.uid ?? null,
    code: ch.code ?? null,
    controller: ch.player === 1 ? 1 : 0,
    loc: { controller: ch.player === 1 ? 1 : 0, location: LOCATION_MZONE, sequence: ch.seq ?? 0 },
    from: { atk: num(ch.from?.atk), def: num(ch.from?.def), level: num(ch.from?.level) },
    to: { atk: num(ch.to?.atk), def: num(ch.to?.def), level: num(ch.to?.level) },
    base: { atk: num(ch.base?.atk), def: num(ch.base?.def) },
  }));
}

/**
 * @param events `OcgGetState` の events（`index` の昇順）
 * @param boardSteps `OcgGetState` の board_steps
 * @returns `{ events, boardSteps }`（変わらなければ同じ参照）
 */
export function withStatChangeEvents({ events = [], boardSteps = null } = {}) {
  const steps = boardSteps?.steps ?? [];
  if (!steps.some((s) => s?.stat_changes?.length)) return { events, boardSteps };
  const outSteps = [];
  /** 元のイベントの位置（配列の添字）→ その直後に入れる疑似イベント */
  const after = new Map();
  let e = 0;
  let lastCore = -1;
  steps.forEach((step) => {
    const changes = step?.stat_changes ?? [];
    if (!changes.length) { outSteps.push(step); return; }
    // そのステップ以前の最後の core のイベント
    while (e < events.length && events[e].index <= step.index) {
      if (events[e].kind === 'event') lastCore = e;
      e += 1;
    }
    const idx = step.index + 0.5;
    const changed = new Set(changes.map((c) => c.uid));
    const baseOf = new Map(changes.map((c) => [c.uid, c.base ?? {}]));
    const moved = {};
    const kept = {};
    Object.entries(step.stats ?? {}).forEach(([uid, v]) => {
      if (!changed.has(uid)) { kept[uid] = v; return; }
      const b = baseOf.get(uid);
      moved[uid] = { ...v, base_atk: num(v?.base_atk) ?? num(b.atk), base_def: num(v?.base_def) ?? num(b.def) };
    });
    // 元のステップ（変わったカードの stats と stat_changes を抜く）。ほかに何も無ければ落とす
    const { stats: _s, stat_changes: _c, ...rest } = step;
    const orig = { ...rest, ...(Object.keys(kept).length ? { stats: kept } : {}) };
    if (Object.keys(orig).length > 1) outSteps.push(orig);
    outSteps.push({ index: idx, stats: moved });
    const base = lastCore >= 0 ? events[lastCore] : null;
    const ev = {
      id: `${base?.id ?? 'start'}:stat`,
      index: idx,
      kind: 'event',
      type: STAT_CHANGE_TYPE,
      turn: base?.turn ?? 0,
      turn_player: base?.turn_player ?? 0,
      phase: base?.phase ?? null,
      chain_stack: base?.chain_stack ?? [],
      solving: base?.solving ?? null,
      op_seq: base?.op_seq ?? null,
      msg_offset: STAT_CHANGE_OFFSET + (base?.msg_offset ?? 0),
      data: { cards: statCards(changes) },
    };
    const list = after.get(lastCore) ?? [];
    list.push(ev);
    after.set(lastCore, list);
  });
  const outEvents = [...(after.get(-1) ?? [])];
  events.forEach((ev, i) => {
    outEvents.push(ev);
    (after.get(i) ?? []).forEach((x) => outEvents.push(x));
  });
  return { events: outEvents, boardSteps: { ...boardSteps, steps: outSteps } };
}
